import { EnvConfig } from '@config';
import { ApiResponse } from 'types/apiResponse';
import { defaultDurationAndCost, DurationAndCostDTO } from 'types/durationAndCost';
import { MnemonicsMappingDTO } from 'types/mnemonicsMapping';
import { WellDetailsDTO, WellFullDetailsDTO } from 'types/well';
import { Contact, Wellbore } from 'types/wellbore';

import Client from './client';

const wellsApiBaseUrl = EnvConfig.drillx_so_api_url || '';
const accept = 'application/json';

// TODO: Once the merge of the microservices is finished, rename / merge this
class WellsApiClient {
  client: Client;

  constructor() {
    this.client = new Client(wellsApiBaseUrl, accept);
  }

  getAllSensorsWellbores = async (): Promise<Wellbore[]> => {
    try {
      const { wellbores }: { wellbores: Wellbore[] } = await this.client.get('/wellbores/sensors');

      return wellbores;
    } catch {
      return [];
    }
  };

  getAllWellbores = async (): Promise<Partial<Wellbore>[]> => {
    try {
      const response = await this.client.get('/wellbores/all');

      return response;
    } catch (error) {
      throw error;
    }
  };

  updateWellbore = async (
    wellboreId: string,
    body: Record<string, string | number | boolean | Contact[] | null | undefined>,
  ): Promise<Wellbore | null> => {
    try {
      const user = await this.client.patch(`/wellbores/${wellboreId}`, body);

      return user;
    } catch {
      return null;
    }
  };

  updateBulkWellbore = async (
    updateObject:
      | Array<any>
      | Record<string, string | number | Array<any> | boolean | null | undefined>,
  ): Promise<Wellbore | null> => {
    try {
      const user = await this.client.patch(`/wellbores/updateBulkWells`, { updateObject });

      return user;
    } catch {
      return null;
    }
  };

  getWellDetails = async (tuwi: string, reportDate: string): Promise<WellDetailsDTO | null> => {
    try {
      return await this.client.get(`/drillx-cockpit/well-details/${tuwi}?reportDate=${reportDate}`);
    } catch {
      return null;
    }
  };

  getDurationAndCost = async (tuwi: string): Promise<DurationAndCostDTO> => {
    try {
      return await this.client.get(`/drillx-cockpit/${tuwi}/duration-and-cost`);
    } catch {
      return defaultDurationAndCost;
    }
  };

  getMnemonicsMapping = async (wellboreId: string): Promise<ApiResponse<MnemonicsMappingDTO>> => {
    try {
      const url = `/wellbores/${wellboreId}/mnemonics-mapping`;
      const response = await this.client.get(url);

      return { response };
    } catch (error) {
      return {
        error,
        response: {
          depthData: {},
          timeData: {},
        },
      };
    }
  };

  patchMnemonicMapping = async (
    wellboreId: string,
    stdMnemonic: string,
    rawMnemonic: string | null,
    rawUnit: string | null,
  ): Promise<MnemonicsMappingDTO> => {
    try {
      const url = `/wellbores/${wellboreId}/mnemonics-mapping`;

      return await this.client.patch(url, {
        stdMnemonic,
        rawMnemonic,
        rawUnit,
      });
    } catch {
      return {
        depthData: {},
        timeData: {},
      };
    }
  };

  getEmptyMnemonicsMapping = async (
    wellboreId: string,
    criticalMnemonics: string[],
  ): Promise<ApiResponse<string[]>> => {
    const mnemonics = await this.getMnemonicsMapping(wellboreId);

    if (mnemonics.error) {
      return { ...mnemonics, response: [] };
    }

    const mnemonicWithoutMapping = criticalMnemonics.filter(stdMnemonic => {
      const depthMnemonic = mnemonics.response.depthData[stdMnemonic];
      const timeMnemonic = mnemonics.response.timeData[stdMnemonic];

      if (!depthMnemonic && !timeMnemonic) return true;

      if (depthMnemonic && !timeMnemonic) return !depthMnemonic.live;

      return !timeMnemonic.live;
    });

    return { response: mnemonicWithoutMapping };
  };

  getWellsWithWellbores = async (): Promise<ApiResponse<WellFullDetailsDTO[]>> => {
    try {
      const url = `/wells/with-wellbores/all`;
      const response = await this.client.get(url);

      return { response };
    } catch (error) {
      return { response: [], error };
    }
  };
}

export default new WellsApiClient();
